'use client';

import { DataTable } from '@/components/data-table/data-table';
import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import { PaymentResourceBadge } from '@/components/payments/PaymentResourceBadge';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ResourceBadge } from '@/components/ui/resource-badge';
import { useDataTable } from '@/hooks/use-data-table';
import { CustomerDetail } from '@/lib/api/customers';
import { DateDisplay } from '@/components/date-display';
import { formatMoney } from "@/lib/money";
import { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

interface CustomerPaymentsProps {
  customer: CustomerDetail;
}

export function CustomerPayments({ customer }: CustomerPaymentsProps) {
  const { t } = useTranslation('customers');
  // Transform customer payments data to match the expected format
  const payments =
    customer.payments?.map((payment) => ({
      id: payment.id,
      status: payment.status,
      amount: payment.amount,
      currency: payment.currency,
      subStatus: payment.subStatus,
      createdAt: payment.createdAt,
      storeId: customer.storeId || '',
      storeName: customer.store?.name || '',
      email: customer.email || null,
      customer: `${customer.firstName} ${customer.lastName}`.trim() || customer.email || '',
      initiatedBy: payment.initiatedBy || '',
      chainIndex: payment.chainIndex || 0,
      retryIndex: payment.retryIndex || 0,
    })) || [];

  const columns: ColumnDef<any>[] = [
    {
      id: 'id',
      accessorKey: 'id',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('paymentHistory.columns.paymentId')} />,
      cell: ({ row }) => {
        const payment = row.original;
        return <PaymentResourceBadge paymentId={payment.id} />;
      },
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('paymentHistory.columns.status')} />,
      cell: ({ row }) => {
        const payment = row.original;
        return (
          <Badge variant="secondary" className="text-xs">
            {payment.status}
          </Badge>
        );
      },
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'amount',
      accessorKey: 'amount',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('paymentHistory.columns.amount')} />,
      cell: ({ row }) => {
        const payment = row.original;
        return <div className="text-sm font-medium">{formatMoney(payment.amount, payment.currency)}</div>;
      },
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'createdAt',
      accessorKey: 'createdAt',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('paymentHistory.columns.createdAt')} />,
      cell: ({ row }) => (
        <DateDisplay
          value={row.original.createdAt}
          format="MM/dd/yyyy"
          className="text-sm"
          fallback="-"
        />
      ),
      enableSorting: true,
      enableHiding: true,
    },
  ];

  const { table } = useDataTable({
    data: payments,
    columns,
    total: payments.length,
    config: { clientMode: true, defaultSorting: [{ id: 'createdAt', desc: true }] },
    onTableStateChange: () => {},
    getRowId: (row: any) => row.id,
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('paymentHistory.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          {payments.length === 0 ? (
            <div className="text-muted-foreground py-8 text-center">{t('paymentHistory.empty')}</div>
          ) : (
            <DataTable
              table={table}
              isLoading={false}
              showFilters={false}
              showToolbar={false}
              showPagination={true}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
